// src/app/monitoring/errorReporter.ts
//
// Internal error-reporting abstraction. No external service (Sentry
// etc.) is wired in yet — this only establishes the shape/contract so
// a real provider can be swapped in later by changing `emit()` only;
// every call site (ErrorBoundary, apiClient, feature code) stays the
// same.
//
// SAFETY: never pass request/response bodies, headers, tokens, or
// passwords into these functions. Only pass whitelisted primitives
// (message, stack, route, endpoint, status, userId). This module is
// the only place `console.error` is allowed to appear in application
// code — everywhere else should call through here instead.

export interface ErrorContext {
  route?: string | undefined;
  userId?: string | undefined;
  [key: string]: unknown;
}

export interface ApiErrorRequestInfo {
  endpoint: string;
  method?: string | undefined;
  status?: number | undefined;
}

export interface CrashComponentInfo {
  componentStack?: string | undefined;
}

type ReportKind = 'error' | 'api_error' | 'crash';

interface ReportPayload {
  kind: ReportKind;
  timestamp: string;
  message: string;
  stack?: string | undefined;
  route?: string | undefined;
  userId?: string | undefined;
  endpoint?: string | undefined;
  method?: string | undefined;
  status?: number | undefined;
  componentStack?: string | undefined;
}

function currentRoute(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.pathname;
}

function toMessageAndStack(error: unknown): { message: string; stack?: string | undefined } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  return { message: String(error) };
}

/** Reports a generic unexpected exception (not an API failure or a render crash). */
export function reportError(error: unknown, context?: ErrorContext): void {
  const { message, stack } = toMessageAndStack(error);
  emit({
    kind: 'error',
    timestamp: new Date().toISOString(),
    message,
    stack,
    route: context?.route ?? currentRoute(),
    userId: context?.userId,
  });
}

/** Reports a failed API request: network error, client-side timeout, or a non-2xx response. */
export function reportApiError(error: unknown, requestInfo?: ApiErrorRequestInfo): void {
  const { message, stack } = toMessageAndStack(error);
  emit({
    kind: 'api_error',
    timestamp: new Date().toISOString(),
    message,
    stack,
    route: currentRoute(),
    endpoint: requestInfo?.endpoint,
    method: requestInfo?.method,
    status: requestInfo?.status,
  });
}

/** Reports a React render crash caught by ErrorBoundary. */
export function reportCrash(error: unknown, componentInfo?: CrashComponentInfo): void {
  const { message, stack } = toMessageAndStack(error);
  emit({
    kind: 'crash',
    timestamp: new Date().toISOString(),
    message,
    stack,
    route: currentRoute(),
    componentStack: componentInfo?.componentStack,
  });
}

/**
 * Single emission point. This is the only intended place in
 * application code where `console.error` is used for production
 * logging — swapping in a real provider later means changing only
 * this function.
 */
function emit(payload: ReportPayload): void {
  console.error('[monitoring]', payload);
}
